import Joi from "joi";
import * as PropTypes from "prop-types";
import React, {Fragment, useEffect, useState} from "react";
import {handleApiException, useHttp} from "../../../services/HttpService";
import {logger} from "../../../services/LogService";
import {createCalculatedColumn, createColumn, createComponentColumn} from "../../../utils/ColumnUtils";
import {useForm} from "../../../utils/FormUtils";
import {requiredNumber} from "../../../utils/Validator";
import EAButton from "../../EAButton";
import EAButtonBar from "../../EAButtonBar";
import EADialog from "../../EADialog";
import EATable from "../../EATable";
import EATextField from "../../EATextField";
import EATableActionButton from "../../tableComponents/EATableActionButton";
import {getAbwasseranlagenToChangeOnStillegen, getAlternativeAbwasseranlagenAndererBetriebe} from "../../../services/BetriebeService";
import {getAlternativeKommunaleAbwasseranlagenLocal} from "../../../services/AnlagenService";

const id = "stilllegenDialog";

function TableDialog(props) {
    let [{data, loading}] = useHttp({
        url: props.betriebsId ? getAlternativeAbwasseranlagenAndererBetriebe(props.betriebsId) : getAlternativeKommunaleAbwasseranlagenLocal,
        timeout: 600000,
    });
    const [filteredData, setFilteredData] = useState([]);
    const [filter, setFilter] = useState("");

    useEffect(() => {
        if (data) {
            setFilteredData([
                ...data.filter((item) => {
                    return (
                        item?.id !== props?.anlageId &&
                        (!filter ||
                            item.betrieb.value.toLowerCase().includes(filter.toLowerCase()) ||
                            item.bezeichnung.toLowerCase().includes(filter.toLowerCase()))
                    );
                }),
            ]);
        }
    }, [data, filter, loading, props.anlageId]);

    return (
        <EADialog
            open={props.open}
            titleChildren={"Aufnehmende Anlage auswählen"}
            content={
                <div style={{width: "700px", maxHeight: "520px", minHeight: "400px"}}>
                    <div className="gridl" style={{gridTemplateColumns: "repeat(3,auto)", paddingBottom: "8px"}}>
                        <EATextField
                            value={filter}
                            onChange={(e) => {
                                e.preventDefault();
                                setFilter(e.target.value);
                            }}
                            id="aufnahmeFilter"
                            data-cy="aufnahmeFilter"
                            label="Suchkriterium"
                        />
                        <div>
                            <EAButton
                                onClick={() => {
                                    setFilter("");
                                }}
                                id="aufnahmeFilterSucheReset"
                                data-cy="aufnahmeFilterSucheReset"
                                label="Zurücksetzen"
                            />
                        </div>
                    </div>
                    <EATable
                        id="aufnahmeAuswahl"
                        initialSortColumn="betrieb"
                        columns={[
                            createComponentColumn(
                                "actions",
                                (row, index) => {
                                    return (
                                        <EATableActionButton id={"selectAufnahme-" + index} iconName="ico-check">
                                            <span
                                                onClick={() => {
                                                    setFilter("");
                                                    props.onClickOk(row);
                                                }}
                                                className={"w-100"}
                                                style={{
                                                    height: "100%",
                                                    display: "block",
                                                }}
                                            />
                                        </EATableActionButton>
                                    );
                                },
                                "",
                                null,
                                {width: "50px", padding: "0"}
                            ),
                            createCalculatedColumn(
                                "betrieb",
                                (row) => {
                                    return row.betrieb.value;
                                },
                                "Betrieb"
                            ),
                            createColumn("bezeichnung", "Abwasseranlage"),
                        ]}
                        data={filteredData}
                        loading={loading}
                    />
                </div>
            }
            buttons={
                <EAButtonBar>
                    <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={props.onClickCancel}/>
                </EAButtonBar>
            }
        />
    );
}

TableDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClickCancel: PropTypes.func.isRequired,
    onClickOk: PropTypes.func.isRequired,
};

function StilllegenDialog({open, betriebsId, anlage, onOk, onCancel}) {
    const [{loading}, fetchAnlagen] = useHttp(
        {url: getAbwasseranlagenToChangeOnStillegen(betriebsId), timeout: 60000},
        {
            manual: true,
        }
    );
    const [dlgOpen, setDlgOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const {values, setValues, formFactory, validate} = useForm({
        schema: {
            anlagen: Joi.array()
                .required()
                .items(
                    Joi.object({
                        id: requiredNumber(),
                        aufnahme: Joi.object({
                            id: requiredNumber(),
                        }).required(),
                    })
                )
                .error((errors) => {
                    errors[0].path = ["anlagen"];
                    errors[0].message = "Für jede Kläranlage des Betriebs muss eine Ersatzkläranlage ausgewählt werden!";
                    return errors[0];
                }),
        },
    });

    useEffect(() => {
        if (!open) {
            return;
        }
        if (!anlage) {
            fetchAnlagen()
                .then(({data}) => {
                    setValues({
                        anlagen: data.map((tmpAnlage) => {
                            tmpAnlage.value = tmpAnlage.bezeichnung;
                            tmpAnlage.aufnahme = null;
                            return tmpAnlage;
                        }),
                    });
                })
                .catch((e) => handleApiException(e, "Error loading stillzulegende Anlagen"));
        } else {
            const anlagenToSet = {};
            anlagenToSet.anlagen = [].concat(anlage);
            setValues(anlagenToSet);
        }
    }, [open, anlage, fetchAnlagen, setValues]);

    function handleClose() {
        setDlgOpen(false);
    }

    function handleCloseOk(tmpAnlage) {
        setDlgOpen(false);
        logger.debug("AnlageClose", tmpAnlage);
        tmpAnlage.value = tmpAnlage.bezeichnung;
        const anlagenToSet = {...values};
        anlagenToSet.anlagen[selectedIndex].aufnahme = tmpAnlage;
        setValues(anlagenToSet);
    }

    return (
        <EADialog
            open={open}
            titleChildren={"Auswahl der aufnehmenden Anlage" + (values?.anlagen?.length > 1 ? "n" : "")}
            content={
                <Fragment>
                    {dlgOpen && (
                        <TableDialog
                            open={dlgOpen}
                            betriebsId={betriebsId}
                            anlageId={anlage?.id}
                            onClickCancel={handleClose}
                            onClickOk={handleCloseOk}
                        />
                    )}
                    <EATable
                        id="anlagenToSwitch"
                        initialSortColumn="mipNr"
                        columns={[
                            createColumn("value", "Anlage"),
                            createComponentColumn(
                                "aufnahme",
                                (row, index) => {
                                    return (
                                        <div className="gridl" style={{gridTemplateColumns: "1fr auto"}}>
                                            <EATextField
                                                id="aufnahme.value"
                                                disabled={true}
                                                value={row?.aufnahme?.value ? row?.aufnahme?.value : ""}
                                            />
                                            {formFactory.renderButton("aufnameAendern", "aufnameAendern", "auswählen...", () => {
                                                setSelectedIndex(index);
                                                setDlgOpen(true);
                                            })}
                                        </div>
                                    );
                                },
                                "Aufnahme"
                            ),
                        ]}
                        loading={loading}
                        data={values?.anlagen}
                    />
                    {formFactory.renderError("anlagen", "stillegenError")}
                </Fragment>
            }
            buttons={
                <EAButtonBar style={{width: "800px"}}>
                    <EAButton id={id + "-cancel"} label={"Abbrechen"} onClick={onCancel}/>
                    <EAButton
                        id={id + "-ok"}
                        label={"Ok"}
                        onClick={() => {
                            if (validate()) return;
                            onOk(values.anlagen);
                        }}
                        color="primary"
                    />
                </EAButtonBar>
            }
        />
    );
}

export default StilllegenDialog;
